import {
    Alert,
    AlertDescription
} from "@solomon-apps/shared/src/components/ui/alert";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import {
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@solomon-apps/shared/src/components/ui/dialog";
import { Input } from "@solomon-apps/shared/src/components/ui/input";
import { Label } from "@solomon-apps/shared/src/components/ui/label";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useEffect, useRef, useState } from "react";

import type {
    ClientApiKeyMetadata,
    CreateClientApiKeyInput
} from "@/services/clientApiKeys";

import { expiryInputToUtc } from "./apiKeyPageModel";

export function CreateKeyDialog({
    disabled,
    error,
    isPending,
    onCreate
}: {
    disabled: boolean;
    error: string | null;
    isPending: boolean;
    onCreate: (input: CreateClientApiKeyInput) => Promise<boolean>;
}): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState("");
    const [expiry, setExpiry] = useState("");

    const close = (): void => {
        setOpen(false);
        setLabel("");
        setExpiry("");
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => (next ? setOpen(true) : close())}
        >
            <Button size="sm" disabled={disabled} onClick={() => setOpen(true)}>
                Create key
            </Button>
            <DialogContent
                status={isPending ? "pending" : error ? "error" : "idle"}
                className="p-0"
            >
                <DialogHeader className="pr-12">
                    <DialogTitle>Create API key</DialogTitle>
                    <DialogDescription>
                        The key belongs on your backend. Do not put it in
                        browser code.
                    </DialogDescription>
                    <DialogCloseButton disabled={isPending} />
                </DialogHeader>
                <form
                    className="grid gap-4 px-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        const expiresAt = expiryInputToUtc(expiry);
                        void onCreate({ label, expiresAt }).then(
                            (created) => created && close()
                        );
                    }}
                >
                    <div className="grid gap-1.5">
                        <Label htmlFor="api-key-label">Key name</Label>
                        <Input
                            id="api-key-label"
                            name="api-key-label"
                            autoComplete="off"
                            surface="filled"
                            size="md"
                            maxLength={100}
                            required
                            value={label}
                            onChange={(event) => setLabel(event.target.value)}
                            disabled={isPending}
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="api-key-expiry">Expiry, optional</Label>
                        <Input
                            id="api-key-expiry"
                            name="api-key-expiry"
                            autoComplete="off"
                            type="datetime-local"
                            surface="filled"
                            size="md"
                            value={expiry}
                            onChange={(event) => setExpiry(event.target.value)}
                            disabled={isPending}
                        />
                        <Text
                            variant="fine-print"
                            className="text-foreground-muted"
                        >
                            The key does not expire if you leave this empty.
                        </Text>
                    </div>
                    {error ? (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : null}
                    <DialogFooter className="px-0">
                        <Button
                            type="button"
                            variant="sunken"
                            size="sm"
                            disabled={isPending}
                            onClick={close}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            size="sm"
                            disabled={isPending || !label.trim()}
                        >
                            {isPending ? "Creating…" : "Create key"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export function SecretDialog({
    secret,
    rotation,
    onDiscard
}: {
    secret: string | null;
    rotation?: { label: string; endsAt: string } | null;
    onDiscard: () => void;
}): React.ReactElement {
    const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
        "idle"
    );
    const secretRef = useRef(secret);
    secretRef.current = secret;

    useEffect(
        () => (): void => {
            secretRef.current = null;
        },
        []
    );
    useEffect(() => {
        setCopyState("idle");
    }, [secret]);

    return (
        <Dialog
            open={Boolean(secret)}
            onOpenChange={(open) => !open && onDiscard()}
        >
            <DialogContent className="p-0">
                <DialogHeader className="pr-12">
                    <DialogTitle>
                        {rotation
                            ? "Copy your replacement API key"
                            : "Copy your API key"}
                    </DialogTitle>
                    <DialogDescription>
                        This is the only time the full key is shown. Store it in
                        your backend secret manager.
                    </DialogDescription>
                    <DialogCloseButton onClick={onDiscard} />
                </DialogHeader>
                <div className="grid gap-3 px-4">
                    {rotation ? (
                        <Alert variant="warning">
                            <AlertDescription>
                                {rotation.label} remains valid until{" "}
                                {new Intl.DateTimeFormat(undefined, {
                                    dateStyle: "medium",
                                    timeStyle: "short"
                                }).format(new Date(rotation.endsAt))}
                                . Wallet sessions issued by the old key stop
                                working at that cutoff or when you revoke the
                                old key.
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    <textarea
                        aria-label="Full API key"
                        className="min-h-20 w-full resize-none break-all border border-line-base bg-surface-sunken p-3 font-mono text-sm"
                        data-testid="api-key-secret"
                        readOnly
                        value={secret ?? ""}
                        onFocus={(event) => event.currentTarget.select()}
                    />
                    <Button
                        size="sm"
                        onClick={() => {
                            const value = secretRef.current;
                            if (!value) return;
                            const clipboard = navigator.clipboard;
                            if (!clipboard) {
                                setCopyState("failed");
                                return;
                            }
                            void clipboard.writeText(value).then(
                                () => setCopyState("copied"),
                                () => setCopyState("failed")
                            );
                        }}
                    >
                        {copyState === "copied" ? "Copied" : "Copy key"}
                    </Button>
                    {copyState === "failed" ? (
                        <Alert variant="destructive" aria-live="polite">
                            <AlertDescription>
                                Copy failed. Select the key text and copy it
                                before you close this window.
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    <span className="sr-only" aria-live="polite">
                        {copyState === "copied" ? "API key copied" : ""}
                    </span>
                </div>
                <DialogFooter>
                    <Button size="sm" variant="sunken" onClick={onDiscard}>
                        I stored the key
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

interface RotateKeyDialogProps {
    apiKey: ClientApiKeyMetadata | null;
    error: string | null;
    isPending: boolean;
    onClose: () => void;
    onRotate: (
        id: string,
        input: { expiresAt?: string | null }
    ) => Promise<boolean>;
}

export function RotateKeyDialog({
    apiKey,
    error,
    isPending,
    onClose,
    onRotate
}: RotateKeyDialogProps): React.ReactElement {
    const [expiry, setExpiry] = useState("");

    useEffect(() => {
        if (!apiKey) setExpiry("");
    }, [apiKey]);

    return (
        <Dialog
            open={Boolean(apiKey)}
            onOpenChange={(open) => !open && onClose()}
        >
            <DialogContent
                status={isPending ? "pending" : error ? "error" : "idle"}
                className="p-0"
            >
                <DialogHeader className="pr-12">
                    <DialogTitle>Rotate {apiKey?.label}</DialogTitle>
                    <DialogDescription>
                        The old key stays valid for at most 24 hours. An earlier
                        old-key expiry remains in effect. The exact cutoff
                        appears with the replacement key.
                    </DialogDescription>
                    <DialogCloseButton disabled={isPending} />
                </DialogHeader>
                <form
                    className="grid gap-4 px-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (!apiKey) return;
                        void onRotate(apiKey.id, {
                            expiresAt: expiryInputToUtc(expiry)
                        });
                    }}
                >
                    <div className="grid gap-1.5">
                        <Label htmlFor="api-key-rotation-expiry">
                            Replacement expiry, optional
                        </Label>
                        <Input
                            id="api-key-rotation-expiry"
                            name="api-key-rotation-expiry"
                            autoComplete="off"
                            type="datetime-local"
                            surface="filled"
                            size="md"
                            value={expiry}
                            onChange={(event) => setExpiry(event.target.value)}
                            disabled={isPending}
                        />
                        <Text
                            variant="fine-print"
                            className="text-foreground-muted"
                        >
                            The replacement does not expire if you leave this
                            empty.
                        </Text>
                    </div>
                    {error ? (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : null}
                    <DialogFooter className="px-0">
                        <Button
                            type="button"
                            variant="sunken"
                            size="sm"
                            disabled={isPending}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            size="sm"
                            disabled={isPending || !apiKey}
                        >
                            {isPending ? "Rotating…" : "Rotate key"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export function RevokeKeyDialog({
    apiKey,
    error,
    isPending,
    onClose,
    onRevoke
}: {
    apiKey: ClientApiKeyMetadata | null;
    error: string | null;
    isPending: boolean;
    onClose: () => void;
    onRevoke: (id: string) => Promise<void>;
}): React.ReactElement {
    return (
        <Dialog
            open={Boolean(apiKey)}
            onOpenChange={(open) => !open && onClose()}
        >
            <DialogContent
                status={isPending ? "pending" : error ? "error" : "idle"}
                className="p-0"
            >
                <DialogHeader className="pr-12">
                    <DialogTitle>Revoke {apiKey?.label}</DialogTitle>
                    <DialogDescription>
                        This key stops working at once. Wallet sessions issued
                        by this key will also stop working.
                    </DialogDescription>
                    <DialogCloseButton disabled={isPending} />
                </DialogHeader>
                {error ? (
                    <Alert variant="destructive" className="mx-4 w-auto">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : null}
                <DialogFooter>
                    <Button
                        variant="sunken"
                        size="sm"
                        disabled={isPending}
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        disabled={isPending || !apiKey}
                        onClick={() => apiKey && void onRevoke(apiKey.id)}
                    >
                        {isPending ? "Revoking…" : "Revoke key"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
